<!-- src\components\SeasonComponent.vue-->
<template>
  <div class="container mt-3">
    <div class="accordion" :id="`season-${season.id}`">
      <div class="accordion-item">
        <h2 class="accordion-header" :id="`heading-${season.id}`">
          <button
            class="accordion-button collapsed"
            type="button"
            data-bs-toggle="collapse"
            :data-bs-target="`#collapse-${season.id}`"
            :aria-controls="`collapse-${season.id}`"
          >
            <div
              class="d-flex justify-content-between align-items-center w-100"
            >
              <div>
                <strong class="hidden-xs">{{ season.description }}</strong>
                <span class="season-dates">
                  {{ formatDate(season.startDate) }} -
                  {{ formatDate(season.endDate) }}
                </span>
              </div>
            </div>
          </button>
          <div class="btn-group me-3 icon-group">
            <span
              ><i
                v-show="!expiredSeason(season.endDate)"
                class="fa fa-bolt icon-medium hover-yellow"
                role="button"
                data-bs-toggle="modal"
                title="Lightning Action"
                :data-bs-target="`#quickAssignModal-${season.id}`"
              ></i
            ></span>
            <span
              ><i
                class="fa fa-copy icon-medium hover-green"
                role="button"
                data-bs-toggle="modal"
                :data-bs-target="`#copySeasonModal-${season.id}`"
                title="Copy Season"
              ></i
            ></span>
            <span
              ><i
                v-show="!expiredSeason(season.endDate)"
                class="fa fa-pencil"
                role="button"
                title="Edit Season"
                @click="editSeason"
              ></i
            ></span>
            <span :class="`${isSeasonChannelsModified ? 'visible-always' : ''}`"
              ><i
                v-show="!expiredSeason(season.endDate)"
                :class="`fa fa-save icon-medium ${
                  isSeasonChannelsModified ? 'color-blue' : 'color-gray'
                }`"
                role="button"
                title="Save Season"
                @click.stop.prevent="saveSeason"
              ></i
            ></span>
            <span
              ><i
                v-show="expiredSeason(season.endDate)"
                class="fa fa-clock-o text-danger icon-medium"
                role="button"
                title="Season Expired"
              ></i
            ></span>
            <span
              ><i
                v-show="!expiredSeason(season.endDate)"
                class="fa fa-trash icon-medium hover-red"
                @click="deleteSeason"
                role="button"
                title="Delete Season"
              ></i
            ></span>
          </div>
        </h2>
        <div
          :id="`collapse-${season.id}`"
          class="accordion-collapse collapse"
          :aria-labelledby="`heading-${season.id}`"
          :data-bs-parent="`#season-${season.id}`"
        >
          <div class="accordion-body">
            <!-- Your Channels -->
            <div class="accordion mb-4" :id="`yourChannels-listed${season.id}`">
              <div class="accordion-item">
                <button
                  class="accordion-button channel-header"
                  type="button"
                  data-bs-toggle="collapse"
                  :data-bs-target="`#collapseYourChannels${season.id}`"
                  aria-expanded="true"
                  :aria-controls="`collapseYourChannels${season.id}`"
                >
                  Your Channels <i class="fa fa-chevron-down ms-2"></i>
                </button>

                <div
                  :id="`collapseYourChannels${season.id}`"
                  class="accordion-collapse collapse show"
                  :aria-labelledby="`headingYourChannels${season.id}`"
                  :data-bs-parent="`#yourChannels-listed${season.id}`"
                >
                  <div v-if="ownedChannels.length === 0">
                    No channels available.
                  </div>
                  <div v-else>
                    <div
                      class="row py-3"
                      v-for="seasonChannel in ownedChannels"
                      :key="seasonChannel.channel?.id"
                    >
                      <div class="col col-3 col-xl-4 align-middle channel-name">
                        {{ seasonChannel.channel?.description }}
                      </div>
                      <div class="col col-2 col-xl-3 d-flex align-items-center">
                        <select
                          class="form-select"
                          v-model="getChannelConfig(seasonChannel.channel!.id!).strategyId"
                          @change="changeSeason"
                        >
                          <option :value="0">No strategy applied</option>
                          <option
                            v-for="strategy in strategies"
                            :key="strategy.id"
                            :value="strategy.id"
                          >
                            {{ strategy.name }}
                          </option>
                        </select>
                      </div>
                      <div class="col col-auto d-flex align-items-center gap-3">
                        <div class="price-adjustment-cell">
                          <div class="input-group">
                            <span
                              class="input-group-text clickable justify-content-center"
                              :class="{
                                active: !seasonChannel.usePercentage,
                              }"
                              @click="toggleUsePercentage(seasonChannel, false)"
                              >$</span
                            >
                            <input
                              type="text"
                              class="form-control"
                              :class="{
                                'text-end': seasonChannel.usePercentage,
                                'text-start': !seasonChannel.usePercentage,
                              }"
                              :value="getChannelAdjustmentValue(seasonChannel)"
                              @input="
                                updateChannelAdjustment(seasonChannel, $event)
                              "
                            />
                            <span
                              class="input-group-text clickable justify-content-center"
                              :class="{
                                active: seasonChannel.usePercentage,
                              }"
                              @click="toggleUsePercentage(seasonChannel, true)"
                              >%</span
                            >
                          </div>
                        </div>
                        <div class="percent-explanation">
                          <label>
                            <em>{{
                              getAdjustmentValueExplanation(seasonChannel)
                            }}</em>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Third Party Channels -->
            <div
              class="accordion"
              :id="`thirdPartyChannels-listed${season.id}`"
            >
              <div class="accordion-item">
                <button
                  class="accordion-button channel-header"
                  type="button"
                  data-bs-toggle="collapse"
                  :data-bs-target="`#collapseThirdPartyChannels${season.id}`"
                  aria-expanded="true"
                  :aria-controls="`collapseThirdPartyChannels${season.id}`"
                >
                  Third Party Channels <i class="fa fa-chevron-down ms-2"></i>
                </button>

                <div
                  :id="`collapseThirdPartyChannels${season.id}`"
                  class="accordion-collapse collapse show"
                  :aria-labelledby="`headingThirdPartyChannels${season.id}`"
                  :data-bs-parent="`#thirdPartyChannels-listed${season.id}`"
                >
                  <div v-if="thirdPartyChannels.length === 0">
                    No channels available.
                  </div>
                  <div v-else>
                    <div
                      class="row py-3"
                      v-for="seasonChannel in thirdPartyChannels"
                      :key="seasonChannel.channel!.id"
                    >
                      <div class="col col-3 col-xl-4 align-middle channel-name">
                        {{ seasonChannel.channel?.description }}
                      </div>
                      <div class="col col-2 col-xl-3 d-flex align-items-center">
                        <select
                          class="form-select"
                          v-model="getChannelConfig(seasonChannel.channel!.id!).strategyId"
                          @change="changeSeason"
                        >
                          <option :value="0">No strategy applied</option>
                          <option
                            v-for="strategy in strategies"
                            :key="strategy.id"
                            :value="strategy.id"
                          >
                            {{ strategy.name }}
                          </option>
                        </select>
                      </div>
                      <div class="col col-auto d-flex align-items-center gap-3">
                        <div class="price-adjustment-cell">
                          <div class="input-group">
                            <span
                              class="input-group-text clickable justify-content-center"
                              :class="{
                                active: !seasonChannel.usePercentage,
                              }"
                              @click="toggleUsePercentage(seasonChannel, false)"
                              >$</span
                            >
                            <input
                              type="text"
                              class="form-control"
                              :class="{
                                'text-end': seasonChannel.usePercentage,
                                'text-start': !seasonChannel.usePercentage,
                              }"
                              :value="getChannelAdjustmentValue(seasonChannel)"
                              @input="
                                updateChannelAdjustment(seasonChannel, $event)
                              "
                            />
                            <span
                              class="input-group-text clickable justify-content-center"
                              :class="{
                                active: seasonChannel.usePercentage,
                              }"
                              @click="toggleUsePercentage(seasonChannel, true)"
                              >%</span
                            >
                          </div>
                        </div>
                        <div class="percent-explanation">
                          <label>
                            <em>{{
                              getAdjustmentValueExplanation(seasonChannel)
                            }}</em>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- End of Channels -->
          </div>
        </div>
      </div>
    </div>
    <SeasonsCopyComponent
      :season="season"
      :existingSeasons="existingSeasons"
      :courseId="courseId"
      @season-copied="onSeasonCopied"
    />
    <SeasonsEditComponent
      :season="season"
      :existingSeasons="existingSeasons"
      @season-updated="onSeasonUpdated"
    />
    <QuickAssignModal
      :seasonId="season.id"
      :seasonName="season.description"
      :strategies="strategies"
      :ownedChannels="ownedChannels"
      :thirdPartyChannels="thirdPartyChannels"
      @apply-assignment="handleApplyAssignment"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref, reactive, computed, watch } from 'vue';
import { Season, Strategy, SeasonChannel } from '../api';
import { seasonsApi, seasonBatchApi } from '../apiClient';
import SeasonsCopyComponent from './SeasonsCopyComponent.vue';
import SeasonsEditComponent from './SeasonsEditComponent.vue';
import { Modal } from 'bootstrap';
import { formatDateToDisplay } from '@/utils/dateUtils';
import QuickAssignModal from './QuickAssignModal.vue';
import { SeasonBatchSaveRequest } from '../api/models';
import { ApplyAssignmentPayload } from '@/types';

export default defineComponent({
  name: 'SeasonComponent',
  components: {
    SeasonsCopyComponent,
    SeasonsEditComponent,
    QuickAssignModal,
  },
  props: {
    season: {
      type: Object as PropType<Season>,
      required: true,
    },
    strategies: {
      type: Array as PropType<Strategy[]>,
      required: true,
    },
    courseId: {
      type: Number,
      required: true,
    },
    existingSeasons: {
      type: Array as PropType<Season[]>,
      default: () => [],
    },
  },
  emits: [
    'season-updated',
    'season-deleted',
    'season-save-error',
    'season-delete-error',
    'season-copied',
  ],
  setup(props, { emit }) {
    const channelConfigs = reactive<{
      [channelId: number]: {
        strategyId: number | null;
        percentAdjust: number;
        amountAdjust: number;
      };
    }>({});

    const seasonChannels = ref<SeasonChannel[]>([]);

    if (props.season.seasonChannels) {
      seasonChannels.value = props.season.seasonChannels.filter(
        (seasonChannel) => seasonChannel.channel?.id != null
      );
      // Initialize channelConfigs
      seasonChannels.value.forEach((seasonChannel) => {
        if (seasonChannel.channel?.id != null) {
          const channelId = seasonChannel.channel.id;
          const strategyId = props.strategies.find(
            (s) => s.id === seasonChannel.strategyId
          )
            ? seasonChannel.strategyId
            : 0;
          channelConfigs[channelId] = {
            strategyId: strategyId ?? 0,
            percentAdjust: seasonChannel.percentAdjust ?? 100,
            amountAdjust: seasonChannel.amountAdjust ?? 0,
          };
        }
      });
    }

    watch(
      () => props.strategies,
      (newStrategies) => {
        if (newStrategies && newStrategies.length > 0) {
          seasonChannels.value.forEach((seasonChannel) => {
            if (seasonChannel.channel?.id != null) {
              const channelId = seasonChannel.channel.id;
              const strategyId = newStrategies.find(
                (strategy) => strategy.id === seasonChannel.strategyId
              )
                ? seasonChannel.strategyId ?? 0
                : 0;

              if (
                channelConfigs[channelId]?.strategyId === 0 &&
                strategyId !== 0
              ) {
                channelConfigs[channelId].strategyId = strategyId;
              }
            }
          });
        }
      }
    );

    const ownedChannels = computed(() =>
      seasonChannels.value.filter(
        (seasonChannel) => seasonChannel.channel?.isCourseOwned
      )
    );

    const thirdPartyChannels = computed(() =>
      seasonChannels.value.filter(
        (seasonChannel) => !seasonChannel.channel?.isCourseOwned
      )
    );

    const onSeasonCopied = (newSeason: Season) => {
      emit('season-copied', newSeason);
    };

    const onSeasonUpdated = (updatedSeason: Season) => {
      emit('season-updated', updatedSeason);
    };

    const isSeasonChannelsModified = ref(false);

    return {
      seasonChannels,
      ownedChannels,
      thirdPartyChannels,
      channelConfigs,
      isSeasonChannelsModified,
      onSeasonCopied,
      onSeasonUpdated,
    };
  },
  methods: {
    changeSeason() {
      this.isSeasonChannelsModified = true;
    },
    handleApplyAssignment(payload: ApplyAssignmentPayload) {
      const { strategy, ownedChannels, thirdPartyChannels } = payload;
      const strategyId =
        typeof strategy === 'string' ? Number(strategy) : strategy;

      // Apply the selected strategy to the specified channels
      [...ownedChannels, ...thirdPartyChannels].forEach((channelId) => {
        // Find the channel configuration and update the strategyId
        if (this.channelConfigs[channelId]) {
          this.channelConfigs[channelId].strategyId = strategyId;
        } else {
          // In case channel configuration doesn't exist yet, initialize it
          this.channelConfigs[channelId] = {
            strategyId: strategyId,
            percentAdjust: 0,
            amountAdjust: 0,
          };
        }
      });

      this.isSeasonChannelsModified = true;
    },
    getChannelConfig(channelId: number) {
      if (!this.channelConfigs[channelId]) {
        this.channelConfigs[channelId] = {
          strategyId: 0,
          percentAdjust: 100,
          amountAdjust: 0,
        };
      }
      return this.channelConfigs[channelId];
    },
    getAdjustmentValueExplanation(seasonChannel: SeasonChannel): string {
      return seasonChannel.usePercentage
        ? (seasonChannel.percentAdjust ?? 0) === 0
          ? '(regular price)'
          : (seasonChannel.percentAdjust ?? 0) > 0
          ? `(${seasonChannel.percentAdjust ?? 0}% increase)`
          : `(${Math.abs(seasonChannel.percentAdjust ?? 0)}% reduction)`
        : (seasonChannel.amountAdjust ?? 0) === 0
        ? '(regular price)'
        : (seasonChannel.amountAdjust ?? 0) > 0
        ? `($${seasonChannel.amountAdjust ?? 0} increase)`
        : `($${Math.abs(seasonChannel.amountAdjust ?? 0)} reduction)`;
    },
    getChannelAdjustmentValue(seasonChannel: SeasonChannel): string {
      const value = seasonChannel.usePercentage
        ? seasonChannel.percentAdjust ?? 0
        : seasonChannel.amountAdjust ?? 0;
      return value.toString();
    },
    updateChannelAdjustment(seasonChannel: SeasonChannel, event: Event) {
      this.changeSeason();
      const inputValue = (event.target as HTMLInputElement).value;

      // Allow any valid number input, including negatives and decimals
      if (/^-?\d*\.?\d*$/.test(inputValue) && inputValue !== '-') {
        const value = inputValue === '' ? null : parseFloat(inputValue);

        if (seasonChannel.usePercentage) {
          seasonChannel.percentAdjust = value;
        } else {
          seasonChannel.amountAdjust = value;
        }
      }
    },
    toggleUsePercentage(seasonChannel: SeasonChannel, usePercentage: boolean) {
      this.changeSeason();
      seasonChannel.usePercentage = usePercentage;

      // Transfer the value between percentAdjust and amountAdjust
      if (usePercentage) {
        seasonChannel.percentAdjust = seasonChannel.amountAdjust;
        seasonChannel.amountAdjust = null;
      } else {
        seasonChannel.amountAdjust = seasonChannel.percentAdjust;
        seasonChannel.percentAdjust = null;
      }
    },
    async saveSeason(event?: Event) {
      if (event) {
        event.stopPropagation();
      }

      if (!this.isSeasonChannelsModified) {
        return;
      }

      const seasonId = this.season.id;

      // Build the SeasonBatchSaveRequest
      const seasonBatchSaveRequest: SeasonBatchSaveRequest = {
        editedSeasons: [
          {
            id: seasonId,
            description: this.season.description,
            startDate: this.season.startDate,
            endDate: this.season.endDate,
            priority: this.season.priority,
            seasonChannels: this.seasonChannels.map((seasonChannel) => {
              const channelId = seasonChannel.channel?.id ?? 0;
              const config = this.channelConfigs[channelId];

              return {
                seasonId: seasonId,
                channelId: channelId,
                strategyId: config.strategyId || -1,
                usePercentage: seasonChannel.usePercentage,
                percentAdjust: seasonChannel.percentAdjust,
                amountAdjust: seasonChannel.amountAdjust,
              };
            }),
          },
        ],
        deletedSeasonIds: [],
      };

      try {
        const response = await seasonBatchApi.seasonBatchSavePut(
          this.courseId,
          seasonBatchSaveRequest
        );
        if (response.status === 200) {
          this.$emit('season-updated', response.data);
        } else {
          console.error('Failed to save season.');
          this.$emit(
            'season-save-error',
            'Failed to save the season. Please try again.'
          );
        }

        this.isSeasonChannelsModified = false;
      } catch (error) {
        console.error('An error occurred while saving the season:', error);
        this.$emit(
          'season-save-error',
          'An error occurred while saving the season. Please try again.'
        );
      }
    },
    editSeason() {
      const modalId = `editSeasonModal-${this.season.id}`;
      const modalElement = document.getElementById(modalId);
      if (modalElement) {
        const modal = new Modal(modalElement);
        modal.show();
      } else {
        console.error(`Modal element with id ${modalId} not found`);
      }
    },
    formatDate(dateString: string | undefined): string {
      if (!dateString) return '';
      return formatDateToDisplay(dateString);
    },
    expiredSeason(endDate: string | undefined): boolean {
      if (!endDate) return false;
      const today = new Date();
      const expDate = new Date(endDate);
      return today > expDate;
    },
    async deleteSeason(): Promise<void> {
      if (confirm('Are you sure you want to delete this season?')) {
        try {
          const response = await seasonsApi.seasonDelete(
            parseInt(this.season.id?.toString() ?? '0')
          );
          if (response.status >= 200 && response.status < 300) {
            this.$emit('season-deleted', this.season.id);
          } else {
            this.$emit(
              'season-delete-error',
              'Failed to delete the season. Please try again.'
            );
          }
        } catch (error: any) {
          const rawMessage =
            error?.response?.data?.message ||
            error?.response?.data?.Message ||
            error?.message;

          const errorMessage =
            typeof rawMessage === 'string'
              ? rawMessage
              : 'Failed to delete the season. Please try again.';

          this.$emit('season-delete-error', errorMessage);
        }
      }
    },
  },
});
</script>

<style scoped>
.accordion-button {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}

.accordion-button:not(.collapsed) {
  background-color: #618183 !important;
  color: white !important;
}

.accordion-button:not(.collapsed) + .btn-group i {
  color: white;
}

.accordion-button:not(.collapsed) + .btn-group i.color-gray {
  color: #e1e1e1;
}

.accordion-button:not(.collapsed) + .btn-group i.color-blue {
  color: #78b9fd;
}

.accordion-button:focus {
  box-shadow: 0 0 0 0.25rem rgba(64, 95, 107, 0.25);
}

.accordion-header .accordion-button {
  padding: 8px 20px !important;
  min-height: 56px;
}

.accordion-body {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}

.accordion .accordion-item {
  margin-bottom: 0.5rem;
}

.accordion-collapse {
  transition: height 0.2s ease;
}

.accordion-item {
  position: relative;
}

.accordion-item:hover .icon-group span,
.accordion-button:not(.collapsed) + .icon-group span {
  opacity: 1;
}

.icon-group span:not(.visible-always) {
  opacity: 0;
  transition: opacity 0.3s ease;
}

.btn-group {
  display: flex;
  align-items: center;
  position: absolute;
  height: 40px;
  top: 8px;
  right: 40px;
  font-size: 1rem;
  z-index: 1000;
}

.btn-group i {
  cursor: pointer;
  color: #405f6b;
  transition: color 0.3s ease;
  padding: 8px;
  font-size: 1.5em;
}

.accordion-button + .btn-group i:hover {
  color: black;
}

.accordion-button + .btn-group i.hover-blue:hover {
  color: #78b9fd;
}

.accordion-button + .btn-group i.hover-green:hover {
  color: #28a745;
}

.accordion-button + .btn-group i.hover-red:hover {
  color: #dc3545;
}

.accordion-button + .btn-group i.hover-yellow:hover {
  color: #ffc107;
}

.accordion-button.collapsed + .btn-group i.color-blue {
  color: #78b9fd;
}

.accordion-button.collapsed + .btn-group i.color-gray {
  color: #e1e1e1;
}

.price-adjustment-cell {
  max-width: 160px;
  display: flex;
  align-items: center;
}

.channel-header {
  padding: 8px 15px;
}

.channel-name {
  color: #405f6b;
  line-height: normal !important;
  margin: auto !important;
  display: flex !important;
  display: -ms-flexbox !important;
  justify-content: flex-start !important;
  margin-left: 15px !important;
  flex-flow: row wrap;
  align-items: center;
}

.dropdown select:disabled {
  background: rgba(64, 95, 107, 0.15) !important;
  color: #618183 !important;
}

.strategy-dd {
  margin-top: 18px !important;
}

table {
  border-bottom: white;
}

.percent-explanation {
  width: 136px !important;
  text-align: left;
  font-weight: normal;
  color: #618183;
}

.season-dates {
  margin-left: 10px;
  color: #618183;
}

.input-group {
  width: 100%;
}

.input-group-text:first-child {
  background-color: #f8f9fa;
  border-right: none;
  cursor: pointer;
  min-width: 42px;
  transition: all 0.3s ease;
}

.input-group-text:last-child {
  background-color: #f8f9fa;
  border-left: none;
  cursor: pointer;
  min-width: 42px;
  transition: all 0.3s ease;
}

.input-group-text:first-child.active,
.input-group-text:last-child.active,
.input-group-text:first-child:hover,
.input-group-text:last-child:hover {
  background-color: var(--bs-border-color);
  border-color: #dbdbdb;
}

.form-control {
  text-align: right;
  padding-right: 25px;
}

.form-control.text-start {
  text-align: left;
  padding-left: 25px;
  padding-right: 5px;
}

.input-group .form-control:focus {
  z-index: 3;
}

.form-control:focus,
.form-select:focus {
  border-color: #618183;
  box-shadow: 0 0 0 0.25rem rgba(64, 95, 107, 0.25);
}

.btn-primary {
  background-color: #405f6b;
  border-color: #405f6b;
}

.btn-primary:hover,
.btn-primary:focus {
  background-color: #618183;
  border-color: #618183;
}

.icon-medium {
  font-size: 1.5em;
}

.icon-large {
  font-size: 2em;
}
</style>
